/** The drawing tools a chart offers, and how many clicks each needs to be placed. */
export const TOOLS = [
  'hline',
  'vline',
  'trend',
  'ray',
  'channel',
  'regression',
  'stddev',
  'pitchfork',
  'fib',
  'fibExpansion',
  'fibFan',
  'fibArcs',
  'fibTimeZones',
  'gannLine',
  'gannFan',
  'gannGrid',
  'elliottImpulse',
  'elliottCorrection',
  'rect',
  'triangle',
  'ellipse',
  'text',
  'priceLabel',
  'arrow',
] as const;
export type Tool = (typeof TOOLS)[number];

export const TOOL_LABELS: Record<Tool, string> = {
  hline: 'Horizontal line',
  vline: 'Vertical line',
  trend: 'Trend line',
  ray: 'Ray',
  channel: 'Equidistant channel',
  regression: 'Linear regression channel',
  stddev: 'Standard deviation channel',
  pitchfork: "Andrews' pitchfork",
  fib: 'Fibonacci retracement',
  fibExpansion: 'Fibonacci expansion',
  fibFan: 'Fibonacci fan',
  fibArcs: 'Fibonacci arcs',
  fibTimeZones: 'Fibonacci time zones',
  gannLine: 'Gann line',
  gannFan: 'Gann fan',
  gannGrid: 'Gann grid',
  elliottImpulse: 'Elliott impulse wave (1-5)',
  elliottCorrection: 'Elliott correction wave (a-c)',
  rect: 'Rectangle',
  triangle: 'Triangle',
  ellipse: 'Ellipse',
  text: 'Text label',
  priceLabel: 'Price label',
  arrow: 'Arrow',
};

export const POINTS_NEEDED: Record<Tool, number> = {
  hline: 1,
  vline: 1,
  trend: 2,
  ray: 2,
  channel: 3,
  regression: 2,
  stddev: 2,
  pitchfork: 3,
  fib: 2,
  fibExpansion: 3,
  fibFan: 2,
  fibArcs: 2,
  fibTimeZones: 2,
  gannLine: 2,
  gannFan: 2,
  gannGrid: 2,
  elliottImpulse: 5,
  elliottCorrection: 3,
  rect: 2,
  triangle: 3,
  ellipse: 2,
  text: 1,
  priceLabel: 1,
  arrow: 2,
};

/** The tools whose lines can run past their anchors (the Extend setting). */
export const EXTENDABLE: readonly Tool[] = ['trend', 'channel', 'fib', 'regression', 'stddev'];

export type LineStyle = 'solid' | 'dashed' | 'dotted';
export type Extend = 'none' | 'right' | 'both';

/** A place on the chart: a time (server-clock seconds) and a price, so a drawing stays put on every timeframe. */
export interface Anchor {
  t: number;
  p: number;
}

export interface Drawing {
  id: string;
  tool: Tool;
  points: Anchor[];
  color: string;
  width: 1 | 2 | 3;
  style: LineStyle;
  /** For trend lines and channels; rays always extend right. */
  extend: Extend;
  text?: string;
}

export const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1] as const;
/** MT5's Fibonacci expansion levels, projected from the third point by the first move. */
export const EXPANSION_LEVELS = [0, 0.618, 1, 1.618, 2.618] as const;
export const FAN_LEVELS = [0.382, 0.5, 0.618] as const;
export const ARC_LEVELS = [0.382, 0.5, 0.618, 1] as const;
export const TIME_ZONES = [0, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89] as const;
/** Gann's angles: price units per time unit, against the 1x1 line through the two points. */
export const GANN_ANGLES: readonly [string, number][] = [
  ['1x8', 1 / 8],
  ['1x4', 1 / 4],
  ['1x3', 1 / 3],
  ['1x2', 1 / 2],
  ['1x1', 1],
  ['2x1', 2],
  ['3x1', 3],
  ['4x1', 4],
  ['8x1', 8],
];
/** How many standard deviations the standard deviation channel is wide on each side (MT5's default). */
export const STDDEV_WIDTH = 1;

/** At most this many drawings per symbol, so the saved preferences (48 KB in all) cannot be filled by one chart. */
export const MAX_DRAWINGS_PER_SYMBOL = 100;

const HEX = /^#[0-9a-fA-F]{6}$/;
const id = () => `d${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;

export function createDrawing(tool: Tool, points: Anchor[], color: string): Drawing {
  return {
    id: id(),
    tool,
    points: points.slice(0, POINTS_NEEDED[tool]),
    color,
    width: 1,
    style: tool === 'fib' || tool === 'fibExpansion' || tool === 'fibTimeZones' ? 'dotted' : 'solid',
    extend: tool === 'regression' || tool === 'stddev' ? 'right' : 'none',
    ...(tool === 'text' ? { text: 'Text' } : {}),
  };
}

/** Reads drawings from stored data, which may be old or damaged: anything unusable is dropped, the rest repaired. */
export function normalizeDrawings(raw: unknown): Drawing[] {
  if (!Array.isArray(raw)) return [];
  const out: Drawing[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const d = item as Partial<Drawing>;
    if (typeof d.tool !== 'string' || !(TOOLS as readonly string[]).includes(d.tool)) continue;
    const tool = d.tool as Tool;
    const points = Array.isArray(d.points)
      ? d.points
          .filter((p) => p && Number.isFinite(p.t) && Number.isFinite(p.p))
          .map((p) => ({ t: p.t, p: p.p }))
      : [];
    if (points.length < POINTS_NEEDED[tool]) continue;
    out.push({
      id: typeof d.id === 'string' && d.id ? d.id : id(),
      tool,
      points: points.slice(0, POINTS_NEEDED[tool]),
      color: typeof d.color === 'string' && HEX.test(d.color) ? d.color : '#f5a623',
      width: d.width === 2 || d.width === 3 ? d.width : 1,
      style: d.style === 'dashed' || d.style === 'dotted' ? d.style : 'solid',
      extend: d.extend === 'right' || d.extend === 'both' ? d.extend : 'none',
      ...(tool === 'text' ? { text: typeof d.text === 'string' ? d.text.slice(0, 80) : 'Text' } : {}),
    });
  }
  return out.slice(0, MAX_DRAWINGS_PER_SYMBOL);
}

/** Moves a whole drawing by a time and price offset (dragging its body). */
export const translate = (d: Drawing, dt: number, dp: number): Drawing => ({
  ...d,
  points: d.points.map((p) => ({ t: p.t + dt, p: p.p + dp })),
});

/** Moves one anchor (dragging a handle). */
export const moveAnchor = (d: Drawing, index: number, to: Anchor): Drawing => ({
  ...d,
  points: d.points.map((p, i) => (i === index ? { ...to } : p)),
});

// ---------------------------------------------------------------------------------------------------- time axis
/**
 * Converts between times and the chart's logical (bar) positions. Inside the loaded bars it interpolates, so a weekend gap
 * is one bar wide like the chart draws it; outside them it extrapolates by the timeframe, so a line can run into the future.
 */
export class TimeAxis {
  constructor(
    private readonly times: number[],
    private readonly step: number,
  ) {}

  logicalOf(time: number): number {
    const n = this.times.length;
    if (n === 0) return 0;
    const first = this.times[0]!;
    const last = this.times[n - 1]!;
    if (time <= first) return (time - first) / this.step;
    if (time >= last) return n - 1 + (time - last) / this.step;
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (this.times[mid]! <= time) lo = mid;
      else hi = mid;
    }
    return lo + (time - this.times[lo]!) / (this.times[lo + 1]! - this.times[lo]!);
  }

  timeOf(logical: number): number {
    const n = this.times.length;
    if (n === 0) return 0;
    if (logical <= 0) return this.times[0]! + logical * this.step;
    if (logical >= n - 1) return this.times[n - 1]! + (logical - (n - 1)) * this.step;
    const i = Math.floor(logical);
    return this.times[i]! + (logical - i) * (this.times[i + 1]! - this.times[i]!);
  }
}

// ---------------------------------------------------------------------------------------------------- geometry
/** What the chart can convert: anchors to pixels and back, and how big the drawing area is. */
export interface Mapper {
  x(time: number): number | null;
  y(price: number): number | null;
  time(x: number): number | null;
  price(y: number): number | null;
  width: number;
  height: number;
  /** The closes of the loaded bars, oldest first, for the regression and standard deviation channels. */
  bars?: readonly { t: number; c: number }[];
}

export type Shape =
  | { kind: 'line'; x1: number; y1: number; x2: number; y2: number; arrow?: boolean; label?: string }
  | { kind: 'rect'; x: number; y: number; w: number; h: number }
  | { kind: 'text'; x: number; y: number; text: string; boxed?: boolean }
  | { kind: 'fill'; points: [number, number][] }
  /** An ellipse, or the part of it from angle `from` to `to` (radians, clockwise from 3 o'clock, as canvas draws). */
  | {
      kind: 'ellipse';
      cx: number;
      cy: number;
      rx: number;
      ry: number;
      from: number;
      to: number;
      fill?: boolean;
    };

/** A least-squares line through the closes between two times: its ends and the widest and typical distance from it. */
export function regressionOf(
  bars: readonly { t: number; c: number }[],
  t1: number,
  t2: number,
): { from: { t: number; p: number }; to: { t: number; p: number }; maxDev: number; stdDev: number } | null {
  const lo = Math.min(t1, t2);
  const hi = Math.max(t1, t2);
  const inside = bars.filter((b) => b.t >= lo && b.t <= hi);
  const n = inside.length;
  if (n < 2) return null;
  let sx = 0;
  let sy = 0;
  let sxy = 0;
  let sxx = 0;
  inside.forEach((b, i) => {
    sx += i;
    sy += b.c;
    sxy += i * b.c;
    sxx += i * i;
  });
  const slope = (n * sxy - sx * sy) / (n * sxx - sx * sx);
  const intercept = (sy - slope * sx) / n;
  let maxDev = 0;
  let sq = 0;
  inside.forEach((b, i) => {
    const dev = b.c - (intercept + slope * i);
    maxDev = Math.max(maxDev, Math.abs(dev));
    sq += dev * dev;
  });
  return {
    from: { t: inside[0]!.t, p: intercept },
    to: { t: inside[n - 1]!.t, p: intercept + slope * (n - 1) },
    maxDev,
    stdDev: Math.sqrt(sq / n),
  };
}

const seg = (x1: number, y1: number, x2: number, y2: number, label?: string): Shape => ({
  kind: 'line',
  x1,
  y1,
  x2,
  y2,
  ...(label ? { label } : {}),
});

/** A line from (x1,y1) through (x2,y2), carried on to the right edge. */
const rayTo = (x1: number, y1: number, x2: number, y2: number, width: number, label?: string): Shape => {
  const l = extendSegment(x1, y1, x2, y2, x2 >= x1 ? 'right' : 'both', width);
  return seg(l.x1, l.y1, l.x2, l.y2, label);
};

/** Extends the line through (x1,y1) and (x2,y2) beyond its ends to the edges of the drawing area. */
export function extendSegment(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  extend: Extend,
  width: number,
): { x1: number; y1: number; x2: number; y2: number } {
  if (extend === 'none' || x1 === x2) return { x1, y1, x2, y2 };
  const slope = (y2 - y1) / (x2 - x1);
  const at = (x: number) => y1 + slope * (x - x1);
  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);
  const startX = extend === 'both' ? Math.min(left, 0) - 1 : left;
  const endX = Math.max(right, width) + 1;
  // Keep the direction the user drew: p1 stays the "from" end.
  return x1 <= x2
    ? { x1: startX, y1: at(startX), x2: endX, y2: at(endX) }
    : { x1: endX, y1: at(endX), x2: startX, y2: at(startX) };
}

/** The shapes to draw for a drawing, in pixels. Null anchors (off the loaded range) make the drawing unavailable. */
export function shapesOf(d: Drawing, m: Mapper): Shape[] {
  const xs = d.points.map((p) => m.x(p.t));
  const ys = d.points.map((p) => m.y(p.p));
  if (xs.some((v) => v === null) || ys.some((v) => v === null)) return [];
  const X = xs as number[];
  const Y = ys as number[];
  const W = m.width;
  const H = m.height;
  switch (d.tool) {
    case 'hline':
      return [{ kind: 'line', x1: 0, y1: Y[0]!, x2: W, y2: Y[0]!, label: d.points[0]!.p.toString() }];
    case 'vline':
      return [{ kind: 'line', x1: X[0]!, y1: 0, x2: X[0]!, y2: H }];
    case 'trend': {
      const l = extendSegment(X[0]!, Y[0]!, X[1]!, Y[1]!, d.extend, W);
      return [{ kind: 'line', ...l }];
    }
    case 'ray': {
      const l = extendSegment(X[0]!, Y[0]!, X[1]!, Y[1]!, X[1]! >= X[0]! ? 'right' : 'both', W);
      return [{ kind: 'line', ...l }];
    }
    case 'arrow':
      return [{ kind: 'line', x1: X[0]!, y1: Y[0]!, x2: X[1]!, y2: Y[1]!, arrow: true }];
    case 'rect':
      return [
        {
          kind: 'rect',
          x: Math.min(X[0]!, X[1]!),
          y: Math.min(Y[0]!, Y[1]!),
          w: Math.abs(X[1]! - X[0]!),
          h: Math.abs(Y[1]! - Y[0]!),
        },
      ];
    case 'channel': {
      const base = extendSegment(X[0]!, Y[0]!, X[1]!, Y[1]!, d.extend, W);
      const slope = X[1]! === X[0]! ? 0 : (Y[1]! - Y[0]!) / (X[1]! - X[0]!);
      const offset = Y[2]! - (Y[0]! + slope * (X[2]! - X[0]!)); // where the third click sits relative to the base line
      const parallel = { x1: base.x1, y1: base.y1 + offset, x2: base.x2, y2: base.y2 + offset };
      return [
        {
          kind: 'fill',
          points: [
            [base.x1, base.y1],
            [base.x2, base.y2],
            [parallel.x2, parallel.y2],
            [parallel.x1, parallel.y1],
          ],
        },
        { kind: 'line', ...base },
        { kind: 'line', ...parallel },
      ];
    }
    case 'fib': {
      const left = d.extend === 'both' ? 0 : Math.min(X[0]!, X[1]!);
      const right = d.extend === 'none' ? Math.max(X[0]!, X[1]!) : W;
      const [p1, p2] = [d.points[0]!.p, d.points[1]!.p];
      const lines: Shape[] = [];
      for (const level of FIB_LEVELS) {
        const price = p2 + (p1 - p2) * level; // 0 % at the end of the move, 100 % at its start
        const y = m.y(price);
        if (y === null) continue;
        lines.push({
          kind: 'line',
          x1: left,
          y1: y,
          x2: right,
          y2: y,
          label: `${(level * 100).toFixed(1)}% ${price.toFixed(5).replace(/0+$/, '')}`,
        });
      }
      return lines;
    }
    case 'text':
      return [{ kind: 'text', x: X[0]!, y: Y[0]!, text: d.text ?? '' }];
    case 'priceLabel':
      return [{ kind: 'text', x: X[0]!, y: Y[0]!, text: priceText(d.points[0]!.p), boxed: true }];
    case 'triangle':
      return [
        { kind: 'fill', points: [0, 1, 2].map((i) => [X[i]!, Y[i]!] as [number, number]) },
        seg(X[0]!, Y[0]!, X[1]!, Y[1]!),
        seg(X[1]!, Y[1]!, X[2]!, Y[2]!),
        seg(X[2]!, Y[2]!, X[0]!, Y[0]!),
      ];
    case 'ellipse':
      return [
        {
          kind: 'ellipse',
          cx: (X[0]! + X[1]!) / 2,
          cy: (Y[0]! + Y[1]!) / 2,
          rx: Math.abs(X[1]! - X[0]!) / 2,
          ry: Math.abs(Y[1]! - Y[0]!) / 2,
          from: 0,
          to: 2 * Math.PI,
          fill: true,
        },
      ];
    case 'pitchfork': {
      // The median line runs from the first point through the middle of the other two; the prongs run parallel to it.
      const mx = (X[1]! + X[2]!) / 2;
      const my = (Y[1]! + Y[2]!) / 2;
      const dx = mx - X[0]!;
      const dy = my - Y[0]!;
      return [
        rayTo(X[0]!, Y[0]!, mx, my, W),
        rayTo(X[1]!, Y[1]!, X[1]! + dx, Y[1]! + dy, W),
        rayTo(X[2]!, Y[2]!, X[2]! + dx, Y[2]! + dy, W),
        seg(X[1]!, Y[1]!, X[2]!, Y[2]!),
      ];
    }
    case 'regression':
    case 'stddev': {
      const fit = m.bars ? regressionOf(m.bars, d.points[0]!.t, d.points[1]!.t) : null;
      if (!fit) return [seg(X[0]!, Y[0]!, X[1]!, Y[1]!)];
      const width = d.tool === 'regression' ? fit.maxDev : fit.stdDev * STDDEV_WIDTH;
      const [x1, x2] = [m.x(fit.from.t), m.x(fit.to.t)];
      const line = (offset: number) => {
        const [y1, y2] = [m.y(fit.from.p + offset), m.y(fit.to.p + offset)];
        if (x1 === null || x2 === null || y1 === null || y2 === null) return null;
        return extendSegment(x1, y1, x2, y2, d.extend, W);
      };
      const [mid, up, down] = [line(0), line(width), line(-width)];
      if (!mid || !up || !down) return [];
      return [
        {
          kind: 'fill',
          points: [
            [up.x1, up.y1],
            [up.x2, up.y2],
            [down.x2, down.y2],
            [down.x1, down.y1],
          ],
        },
        seg(mid.x1, mid.y1, mid.x2, mid.y2),
        seg(up.x1, up.y1, up.x2, up.y2),
        seg(down.x1, down.y1, down.x2, down.y2),
      ];
    }
    case 'fibExpansion': {
      // The move from the first to the second point, projected from the third.
      const move = d.points[1]!.p - d.points[0]!.p;
      const shapes: Shape[] = [seg(X[0]!, Y[0]!, X[1]!, Y[1]!), seg(X[1]!, Y[1]!, X[2]!, Y[2]!)];
      for (const level of EXPANSION_LEVELS) {
        const price = d.points[2]!.p + move * level;
        const y = m.y(price);
        if (y === null) continue;
        shapes.push(seg(X[2]!, y, W, y, `${(level * 100).toFixed(1)}% ${priceText(price)}`));
      }
      return shapes;
    }
    case 'fibFan': {
      const shapes: Shape[] = [seg(X[0]!, Y[0]!, X[1]!, Y[1]!)];
      for (const level of FAN_LEVELS) {
        const y = Y[1]! + (Y[0]! - Y[1]!) * level;
        shapes.push(rayTo(X[0]!, Y[0]!, X[1]!, y, W, `${(level * 100).toFixed(1)}%`));
      }
      return shapes;
    }
    case 'fibArcs': {
      // Arcs round the second point, on the side of the first, at fractions of the distance between them.
      const r = Math.hypot(X[1]! - X[0]!, Y[1]! - Y[0]!);
      const above = Y[0]! < Y[1]!;
      const shapes: Shape[] = [seg(X[0]!, Y[0]!, X[1]!, Y[1]!)];
      for (const level of ARC_LEVELS) {
        shapes.push({
          kind: 'ellipse',
          cx: X[1]!,
          cy: Y[1]!,
          rx: r * level,
          ry: r * level,
          from: above ? Math.PI : 0,
          to: above ? 2 * Math.PI : Math.PI,
        });
      }
      return shapes;
    }
    case 'fibTimeZones': {
      const unit = X[1]! - X[0]!;
      if (unit === 0) return [seg(X[0]!, 0, X[0]!, H)];
      return TIME_ZONES.map((n) => X[0]! + unit * n)
        .filter((x) => x >= -1 && x <= W + 1)
        .map((x, i) => seg(x, 0, x, H, i === 0 ? '0' : String(TIME_ZONES[i])));
    }
    case 'gannLine':
      return [rayTo(X[0]!, Y[0]!, X[1]!, Y[1]!, W, '1x1')];
    case 'gannFan': {
      const dx = X[1]! - X[0]!;
      const dy = Y[1]! - Y[0]!;
      return GANN_ANGLES.map(([name, k]) => rayTo(X[0]!, Y[0]!, X[0]! + dx, Y[0]! + dy * k, W, name));
    }
    case 'gannGrid': {
      // 1x1 lines both ways, one step (the distance between the points in time) apart, across the chart.
      const dx = X[1]! - X[0]!;
      const slope = dx === 0 ? 0 : (Y[1]! - Y[0]!) / dx;
      const step = Math.abs(dx);
      if (step < 4 || slope === 0) return [seg(X[0]!, Y[0]!, X[1]!, Y[1]!)];
      const shapes: Shape[] = [];
      const reach = Math.ceil((W + Math.abs(H / slope)) / step) + 1;
      for (let k = -reach; k <= reach && shapes.length < 400; k++) {
        const x0 = X[0]! + k * step;
        for (const s of [slope, -slope]) {
          const yAt = (x: number) => Y[0]! + s * (x - x0);
          shapes.push(seg(0, yAt(0), W, yAt(W)));
        }
      }
      return shapes;
    }
    case 'elliottImpulse':
    case 'elliottCorrection': {
      const names = d.tool === 'elliottImpulse' ? ['1', '2', '3', '4', '5'] : ['a', 'b', 'c'];
      const shapes: Shape[] = [];
      for (let i = 1; i < X.length; i++) shapes.push(seg(X[i - 1]!, Y[i - 1]!, X[i]!, Y[i]!));
      // Each label goes above a peak and below a trough.
      names.forEach((name, i) => {
        const before = Y[i - 1] ?? Y[i + 1] ?? Y[i]!;
        const peak = Y[i]! <= before;
        shapes.push({ kind: 'text', x: X[i]! - 6, y: peak ? Y[i]! - 8 : Y[i]! + 18, text: `(${name})` });
      });
      return shapes;
    }
  }
}

/** A price as a short label: up to five decimals, without trailing zeros. */
function priceText(price: number): string {
  return Number(price.toFixed(5)).toString();
}

/** Distance from a point to a line segment. */
export function distanceToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lengthSq));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

const TEXT_CHAR_WIDTH = 7;
const TEXT_HEIGHT = 16;

/** How far the point is from the drawing's shapes (0 when on it); Infinity when it has nothing to hit. */
export function distanceToShapes(shapes: Shape[], x: number, y: number): number {
  let best = Infinity;
  for (const s of shapes) {
    if (s.kind === 'line') best = Math.min(best, distanceToSegment(x, y, s.x1, s.y1, s.x2, s.y2));
    else if (s.kind === 'rect') {
      const inside = x >= s.x && x <= s.x + s.w && y >= s.y && y <= s.y + s.h;
      const edges: [number, number, number, number][] = [
        [s.x, s.y, s.x + s.w, s.y],
        [s.x + s.w, s.y, s.x + s.w, s.y + s.h],
        [s.x + s.w, s.y + s.h, s.x, s.y + s.h],
        [s.x, s.y + s.h, s.x, s.y],
      ];
      const edge = Math.min(...edges.map(([a, b, c, e]) => distanceToSegment(x, y, a, b, c, e)));
      best = Math.min(best, inside ? 0 : edge);
    } else if (s.kind === 'ellipse') {
      // walk the outline in short steps
      const steps = 48;
      let px = s.cx + s.rx * Math.cos(s.from);
      let py = s.cy + s.ry * Math.sin(s.from);
      for (let i = 1; i <= steps; i++) {
        const a = s.from + ((s.to - s.from) * i) / steps;
        const nx = s.cx + s.rx * Math.cos(a);
        const ny = s.cy + s.ry * Math.sin(a);
        best = Math.min(best, distanceToSegment(x, y, px, py, nx, ny));
        px = nx;
        py = ny;
      }
      if (s.fill && s.rx > 0 && s.ry > 0 && ((x - s.cx) / s.rx) ** 2 + ((y - s.cy) / s.ry) ** 2 <= 1)
        best = 0;
    } else if (s.kind === 'text') {
      const w = Math.max(1, s.text.length) * TEXT_CHAR_WIDTH;
      const inside = x >= s.x && x <= s.x + w && y >= s.y - TEXT_HEIGHT && y <= s.y;
      best = Math.min(
        best,
        inside
          ? 0
          : Math.hypot(Math.max(s.x - x, 0, x - (s.x + w)), Math.max(s.y - TEXT_HEIGHT - y, 0, y - s.y)),
      );
    }
  }
  return best;
}

/** The pixel positions of a drawing's anchors, for drawing and grabbing its handles. */
export function anchorsOf(d: Drawing, m: Mapper): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (const p of d.points) {
    const x = m.x(p.t);
    const y = m.y(p.p);
    if (x !== null && y !== null) out.push({ x, y });
  }
  return out;
}
